import React, { useContext, useEffect, useState } from 'react';
import Button from '../../components/shared/custom-btn';
import CustomInput from '../../components/shared/custom-input';
import { RxCross2 } from 'react-icons/rx';
import DateTimeLocalInput from '../../components/shared/datetime-input';
import CustomTextarea from '../../components/shared/custom-textarea';
import CustomReactSelect from '../../components/shared/custom-react-selector';
import ModalHeading from '../../components/shared/modal-heading';
import { prependDefault } from '../../utils/structure-options-utils';
import TabToggle from '../../components/shared/tab-toggle';
import { AccountsContext } from '../../components/context/account-context';
import { createExpense } from '../../services/expenses-service';
import { ApiResponseMessage } from '../../components/shared/api-response';
import { localDateTimeToUTC } from '../../utils/set-date-range-utils';
import Loader from '../../components/shared/loader';

const AddModal = ({
    vehicle,
    handleCloseAddModal,
    onAdd,
    openEditModal,
    spendors,
    vendors,
    vehicles,
    categories,
    paymentMethods,
    jobsOptions,
    isVehicleEditable = true,
    fetchExpenses,
    showVehicle = true,
    isJobTab = false,
    job,
    fetchGlobalData,
    globalDataloading
}) => {
    const { accountId, attributes } = useContext(AccountsContext);


    const [formData, setFormData] = useState({
        time: '',
        vehicle: vehicle ? vehicle.vehicleId : '',
        vendor: '',
        job: job ? job.jobId : '',
        spender: '',
        category: '',
        currency: 'EUR',
        baseAmount: '',
        shared: 'Yes',
        paymentMethod: '',
        note: '',
        taxOption: 'Include',
        taxPercentage: '13',
    });

    const [loading, setLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState({ message: '', type: '' });

    // Determine if vehicle or job fields should be disabled
    const isVehicleDisabled = loading || !isVehicleEditable || !!formData.job;
    const isJobDisabled = loading || !!formData.vehicle;

    const calculatedAmount = () => {
        const baseAmount = parseFloat(formData.baseAmount) || 0;
        if (formData.taxOption === 'Exclude' && formData.taxPercentage) {
            const taxPercent = parseFloat(formData.taxPercentage) || 0;
            const tax = (baseAmount * taxPercent) / 100;
            return (baseAmount + tax).toFixed(2);
        }
        return baseAmount.toFixed(2);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setApiResponse({ message: '', type: '' });
    };

    const handleSelectChange = (selectedOption, { name }) => {
        const value = selectedOption ? selectedOption.value : '';
        setFormData((prev) => {
            // If selecting a vehicle, clear job
            if (name === 'vehicle') {
                return { ...prev, vehicle: value, job: '' };
            }
            // If selecting a job, clear vehicle
            if (name === 'job') {
                return { ...prev, job: value, vehicle: '' };
            }
            return { ...prev, [name]: value };
        });
        setApiResponse({ message: '', type: '' });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (loading || apiResponse.type === 'success') return;
        setLoading(true);
        setApiResponse({ message: '', type: '' });
        try {
            const baseAmount = parseFloat(formData.baseAmount) || 0;
            let finalAmount = baseAmount;
            if (formData.taxOption === 'Exclude' && formData.taxPercentage) {
                const taxPercent = parseFloat(formData.taxPercentage) || 0;
                const tax = (baseAmount * taxPercent) / 100;
                finalAmount = baseAmount + tax;
            }
            const expenseData = {
                spentBy: formData.spender,
                vehicleId: formData.vehicle,
                expenseTime: localDateTimeToUTC(formData.time),
                vendorId: formData.vendor,
                jobId: formData.job,
                category: formData.category,
                paymentMethod: formData.paymentMethod,
                amount: finalAmount,
                currency: formData.currency,
                shared: formData.shared === 'Yes',
                note: formData.note,
                ...(formData.taxOption === 'Exclude' && { taxPercentage: parseFloat(formData.taxPercentage) }),
            };
            const response = await createExpense(expenseData, accountId);
            console.log(response);
            if (response.success) {
                setApiResponse({ message: 'Expense record created successfully!', type: 'success' });
                const newItem = response.data.data;
                onAdd?.(newItem);
                fetchExpenses?.('Expenses');
                openEditModal?.(newItem);
                setTimeout(() => {
                    handleCloseAddModal();
                }, 1000);
            } else {
                setApiResponse({
                    message: response.data.message || 'Failed to create expense record',
                    type: 'error',
                });
            }
        } catch (error) {
            setApiResponse({
                message: error.message || 'An unexpected error occurred',
                type: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const vendorOptions = prependDefault(
        vendors?.map((v) => ({ value: v.vendorId, label: v.label })),
        'Vendor'
    );
    const vehicleOptions = vehicles && prependDefault(
        vehicles?.map((v) => ({ value: v.vehicleId, label: v.label })),
        'Vehicle'
    );
    const spenderOptions = prependDefault(
        spendors?.map((s) => ({ value: s.userId, label: s.label })),
        'Spender'
    );
    const categoryOptions = prependDefault(
        categories?.map((c) => ({ value: c.slug, label: c.label })),
        'Category'
    );
    const paymentMethodOptions = prependDefault(
        paymentMethods?.map((p) => ({ value: p.slug, label: p.label })),
        'Payment Method'
    );
    const jobOptions = prependDefault(
        jobsOptions?.map((j) => ({ value: j.jobId, label: j.label })),
        'Jobs'
    );

    useEffect(() => {
        if (typeof fetchGlobalData === 'function') {
            fetchGlobalData();
        }
    }, [])

    return (
        <form onSubmit={handleSave} className="space-y-2 pt-3 relative">
            <ModalHeading className="text-left mb-3 border-b border-gray-300 px-4 pb-2">
                Add Expense Detail
            </ModalHeading>

            <button
                className="hover:scale-105 absolute -top-2 right-2 bg-white border p-2 rounded-full"
                onClick={handleCloseAddModal}
                disabled={loading}
            >
                <RxCross2 className="text-xl" />
            </button>

            <ApiResponseMessage response={apiResponse} />

            {
                globalDataloading ?
                    <Loader />
                    :
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 px-4 gap-y-1.5 gap-x-2">
                            <DateTimeLocalInput
                                label="Time"
                                name="time"
                                value={formData.time}
                                onChange={handleChange}
                                required={true}
                                disabled={loading}
                            />

                            <CustomReactSelect
                                label="Category"
                                name="category"
                                value={categoryOptions.find((opt) => opt.value === formData.category)}
                                options={categoryOptions}
                                onChange={handleSelectChange}
                                placeholder='Select category'
                                required={true}
                                isDisabled={loading}
                            />

                            {
                                showVehicle &&
                                <CustomReactSelect
                                    label="Vehicle"
                                    name="vehicle"
                                    value={vehicleOptions.find((opt) => opt.value === formData.vehicle)}
                                    options={vehicleOptions}
                                    placeholder='Select vehicle'
                                    onChange={handleSelectChange}
                                    isEditable={isVehicleEditable}
                                    isDisabled={isVehicleDisabled}
                                />
                            }


                            <CustomReactSelect
                                label="Job"
                                name="job"
                                value={jobOptions.find((opt) => opt.value === formData.job)}
                                options={jobOptions}
                                onChange={handleSelectChange}
                                placeholder='Select job'
                                isEditable={isVehicleEditable}
                                isDisabled={isJobTab ? true : isJobDisabled}
                            />

                            <CustomReactSelect
                                label="Spender"
                                name="spender"
                                value={spenderOptions.find((opt) => opt.value === formData.spender)}
                                options={spenderOptions}
                                placeholder='Select spender'
                                onChange={handleSelectChange}
                                required={true}
                                isDisabled={loading}
                            />

                            <CustomReactSelect
                                label="Vendor"
                                name="vendor"
                                value={vendorOptions.find((opt) => opt.value === formData.vendor)}
                                options={vendorOptions}
                                placeholder='Select vendor'
                                onChange={handleSelectChange}
                                isDisabled={loading}
                            />

                            <div className="sm:col-span-2 flex w-full sm:flex-nowrap flex-wrap items-center gap-2">
                                <div className="sm:w-[150px] w-full relative">
                                    <CustomInput
                                        label="Amount"
                                        name="baseAmount"
                                        type="number"
                                        value={formData.baseAmount}
                                        onChange={handleChange}
                                        inputStyle={' !py-[3px]'}
                                        labelStyle={'text-gray-700'}
                                        labelGap={'!gap-0.5'}
                                        required={true}
                                        decimalMaxDigits={10}
                                        allowDecimalFormat={true}
                                        disabled={loading}
                                        unit={attributes?.currency}
                                    />
                                    {formData.taxOption === 'Exclude' && formData.baseAmount && (
                                        <p className="text-xs whitespace-nowrap absolute -bottom-4 left-0 text-gray-500 mt-1">
                                            Total with tax: {calculatedAmount()}
                                        </p>
                                    )}
                                </div>

                                <div className="flex items-center gap-2 sm:mt-0 mt-3">
                                    <div className="flex flex-col">
                                        <label className="block text-sm text-gray-700 mb-[3px]">Tax</label>
                                        <TabToggle
                                            options={[
                                                { label: 'Inc', value: 'Include' },
                                                { label: 'Exc', value: 'Exclude' },
                                            ]}
                                            className={'!w-11 '}
                                            selected={formData.taxOption}
                                            onChange={(val) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    taxOption: val,
                                                    taxPercentage: val === 'Exclude' ? (prev.taxPercentage || '13') : '',
                                                }))
                                            }
                                            disabled={loading}
                                        />
                                    </div>

                                    {formData.taxOption === 'Exclude' && (
                                        <div className="relative w-28">
                                            <CustomInput
                                                label="Tax %"
                                                name="taxPercentage"
                                                value={formData.taxPercentage}
                                                onChange={handleChange}
                                                type="number"
                                                inputStyle={' sm:!py-[3px]'}
                                                labelGap={'!gap-0.5'}
                                                labelStyle={'text-gray-700 !text-sm'}
                                                decimalMaxDigits={4}
                                                allowDecimalFormat={true}
                                                disabled={loading}
                                            />
                                            <span className="absolute right-[26px] top-[26px] text-gray-500 font-bold">%</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className={`${formData.taxOption === 'Exclude' ? 'mt-3' : ''}`}>
                                <CustomReactSelect
                                    label="Payment Method"
                                    name="paymentMethod"
                                    value={paymentMethodOptions.find((opt) => opt.value === formData.paymentMethod)}
                                    options={paymentMethodOptions}
                                    placeholder='Select payment method'
                                    onChange={handleSelectChange}
                                    required={true}
                                    isDisabled={loading}
                                />
                            </div>

                            <div className={`flex flex-col ${formData.taxOption === 'Exclude' ? 'mt-3' : ''}`}>
                                <label className="block text-sm text-gray-700 mb-[2.5px]">Shared</label>
                                <TabToggle
                                    options={[
                                        { label: 'Yes', value: 'Yes' },
                                        { label: 'No', value: 'No' },
                                    ]}
                                    className={'!w-11 '}
                                    selected={formData.shared}
                                    onChange={(val) => setFormData((prev) => ({ ...prev, shared: val }))}
                                    disabled={loading}
                                />
                            </div>

                            <div className="w-full sm:col-span-2">
                                <CustomTextarea
                                    label="Note"
                                    name="note"
                                    value={formData.note}
                                    onChange={handleChange}
                                    placeholder=""
                                    rows={2}
                                    labelStyle={'text-gray-700'}
                                    maxLength={500}
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end px-4">
                            <Button
                                label={loading ? 'Saving...' : 'Save'}
                                loading={loading}
                                loadingLabel="Saving..."
                                type="submit"
                                disabled={loading || apiResponse.type === 'success'}
                                className="bg-btnBg rounded-lg !py-1 text-white"
                            />
                        </div>
                    </>
            }

        </form>
    );
};

export default AddModal;